/**
 * Custom hooks for favorites management
 * Uses React Query for data fetching and caching
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as favoritesApi from '../api/favoritesApi';
import type {
  AddFavoriteProviderRequest,
  AddFavoriteProfessionalRequest,
  AddFavoriteResourceRequest,
} from '../types';

// Query keys for React Query cache management
export const favoritesKeys = {
  all: ['favorites'] as const,
  allFavorites: () => [...favoritesKeys.all, 'all'] as const,
  providers: () => [...favoritesKeys.all, 'providers'] as const,
  professionals: () => [...favoritesKeys.all, 'professionals'] as const,
  resources: () => [...favoritesKeys.all, 'resources'] as const,
  providerStatus: (providerId: string) =>
    [...favoritesKeys.all, 'provider-status', providerId] as const,
  professionalStatus: (professionalUserId: string) =>
    [...favoritesKeys.all, 'professional-status', professionalUserId] as const,
  resourceStatus: (resourceId: string) =>
    [...favoritesKeys.all, 'resource-status', resourceId] as const,
  curated: () => [...favoritesKeys.all, 'curated'] as const,
};

/**
 * Hook to fetch all favorites (providers and resources)
 * FR-093: System MUST provide a Favorites page with sections for both types
 */
export const useAllFavorites = () => {
  return useQuery({
    queryKey: favoritesKeys.allFavorites(),
    queryFn: favoritesApi.getAllFavorites,
  });
};

/**
 * Hook to fetch the global bQuest-curated lists (Feature 083).
 * Lives under favoritesKeys.all, so any favorite mutation refetches it too,
 * keeping curated cards' save state in sync with the saved sections.
 */
export const useCuratedTeam = () => {
  return useQuery({
    queryKey: favoritesKeys.curated(),
    queryFn: favoritesApi.getCuratedTeam,
  });
};

/**
 * Hook to fetch favorite providers
 * FR-093: System MUST provide a Favorites page with sections for Favorite Providers
 */
export const useFavoriteProviders = () => {
  return useQuery({
    queryKey: favoritesKeys.providers(),
    queryFn: favoritesApi.getFavoriteProviders,
  });
};

/**
 * Hook to fetch favorite resources
 * FR-093: System MUST provide a Favorites page with sections for Favorite Resources
 */
export const useFavoriteResources = () => {
  return useQuery({
    queryKey: favoritesKeys.resources(),
    queryFn: favoritesApi.getFavoriteResources,
  });
};

/**
 * Hook to add a provider to favorites
 * FR-092: System MUST allow Care Seekers to save favorite providers
 * FR-096: System MUST prevent duplicate favorites
 */
export const useAddFavoriteProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddFavoriteProviderRequest) =>
      favoritesApi.addFavoriteProvider(data),
    onSuccess: () => {
      // Invalidate all favorites queries to refetch
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
    },
  });
};

/**
 * Hook to add a resource to favorites
 * FR-092: System MUST allow Care Seekers to save favorite resources
 * FR-096: System MUST prevent duplicate favorites
 */
export const useAddFavoriteResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddFavoriteResourceRequest) =>
      favoritesApi.addFavoriteResource(data),
    onSuccess: () => {
      // Invalidate all favorites queries to refetch
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
    },
  });
};

/**
 * Hook to remove a provider from favorites
 * FR-095: System MUST allow Care Seekers to remove items from their Favorites
 */
export const useRemoveFavoriteProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (providerId: string) =>
      favoritesApi.removeFavoriteProvider(providerId),
    onSuccess: () => {
      // Invalidate all favorites queries to refetch
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
    },
  });
};

/**
 * Hook to remove a resource from favorites
 * FR-095: System MUST allow Care Seekers to remove items from their Favorites
 */
export const useRemoveFavoriteResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resourceId: string) =>
      favoritesApi.removeFavoriteResource(resourceId),
    onSuccess: () => {
      // Invalidate all favorites queries to refetch
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
    },
  });
};

/**
 * Hook to check if a provider is favorited
 * Helper for UI state management (toggle buttons)
 * @param providerId - The provider ID to check favorite status for
 * @param enabled - Optional flag to enable/disable the query (default: true if providerId exists)
 */
export const useProviderFavoriteStatus = (providerId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: favoritesKeys.providerStatus(providerId),
    queryFn: () => favoritesApi.checkProviderFavoriteStatus(providerId),
    enabled: enabled !== undefined ? enabled && !!providerId : !!providerId,
  });
};

/**
 * Hook to check if a resource is favorited
 * Helper for UI state management (toggle buttons)
 */
export const useResourceFavoriteStatus = (resourceId: string) => {
  return useQuery({
    queryKey: favoritesKeys.resourceStatus(resourceId),
    queryFn: () => favoritesApi.checkResourceFavoriteStatus(resourceId),
    enabled: !!resourceId,
  });
};

// ============================================================================
// FavoriteProfessional Hooks (User Story 2 - Phase 4)
// Supports both Service Providers AND Financial Advisors
// ============================================================================

/**
 * Hook to fetch all favorites with professionals (SP + FA)
 * FR-093: System MUST provide a Favorites page with sections for both types
 */
export const useAllFavoritesWithProfessionals = () => {
  return useQuery({
    queryKey: favoritesKeys.allFavorites(),
    queryFn: favoritesApi.getAllFavoritesWithProfessionals,
  });
};

/**
 * Hook to fetch favorite professionals (SPs and FAs)
 * FR-093: System MUST provide a Favorites page with sections for Favorite Professionals
 */
export const useFavoriteProfessionals = () => {
  return useQuery({
    queryKey: favoritesKeys.professionals(),
    queryFn: favoritesApi.getFavoriteProfessionals,
  });
};

/**
 * Hook to add a professional to favorites
 * FR-FV-002/003: Support favoriting both SPs and FAs
 */
export const useAddFavoriteProfessional = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddFavoriteProfessionalRequest) =>
      favoritesApi.addFavoriteProfessional(data),
    onSuccess: () => {
      // Invalidate all favorites queries to refetch
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
    },
  });
};

/**
 * Hook to remove a professional from favorites
 * FR-095: System MUST allow Care Seekers to remove items from their Favorites
 */
export const useRemoveFavoriteProfessional = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (professionalUserId: string) =>
      favoritesApi.removeFavoriteProfessional(professionalUserId),
    onSuccess: () => {
      // Invalidate all favorites queries to refetch
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
    },
  });
};

/**
 * Hook to check if a professional is favorited
 * Helper for UI state management (toggle buttons)
 * @param professionalUserId - The professional User ID to check favorite status for
 * @param enabled - Optional flag to enable/disable the query
 */
export const useProfessionalFavoriteStatus = (professionalUserId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: favoritesKeys.professionalStatus(professionalUserId),
    queryFn: () => favoritesApi.checkProfessionalFavoriteStatus(professionalUserId),
    enabled: enabled !== undefined ? enabled && !!professionalUserId : !!professionalUserId,
  });
};
